import React, { useEffect } from 'react';
import { SelectField } from '@flos/react-ui';
import { Dropdown as DropdownType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import useAppState from '../../state/appState';
import { getValidationErrors } from '../../validators/validator-mapping';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Dropdown = ({ values, helpText, elementId, caption, placeholder }: DropdownType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const valueObj = Dropdown.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [valueObj]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Dropdown.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);
    return (
        <>
            {caption && <h6>{caption}</h6>}
            <SelectField
                required={isRequiredField}
                label={placeholder}
                id={elementId}
                key={elementId}
                options={values.map((value) => ({
                    label: value.caption,
                    value: value.value
                }))}
                isValid={isValid}
                helpText={helpText}
                errorText={errorMessage}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = event.target.value;
                    const label = values.find((item) => item.value === value)?.caption;
                    setFormValue(elementId, { label, value });
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                value={valueObj.value}
            />
        </>
    );
};

Dropdown.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as DropdownType;
    const { values, summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    const option = values.find((item) => item.value === value.value);
    const caption = option?.caption;

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={caption} />;
};

Dropdown.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Dropdown.getValue(formValues, elementId);

    const label2 = valueObj.label;
    const valueId = { string: valueObj.value };

    return getClaimFormSubmitValue('dropdown', elementId, label, label2, valueId);
};

Dropdown.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Dropdown.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Dropdown.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Dropdown.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = Dropdown.getValue(formValues, elementId);

    return value;
};

Dropdown.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (!valueObject) {
        return false;
    }

    const element = getElementFromElementId(formConfig, elementId) as DropdownType;
    const options = element.values.find((option: any) => option.value === valueObject.value);

    return options ? true : false;
};

Dropdown.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    //const value = Dropdown.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Dropdown.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Dropdown;
